/**
 * Copyright 2021-2023 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import { generateFraasEnvironmentApi } from '@forgerock/platform-shared/src/api/BaseApi';

const requestConfig = {
  headers: {
    'Content-Type': 'application/json',
    'Accept-API-Version': 'protocol=1.0,resource=1.0',
  },
};

/**
 * Gets the existing variables
 * @returns {Promise} Returns a promise that resolves to an array of variables
 */
export function getStartupVariables() {
  return generateFraasEnvironmentApi().get('/variables', requestConfig);
}

/**
 * Create or update a variable
 * @param {String} _id the _id of the variable to create or update
 * @param {String} description the description of the variable to create or update
 * @param {String} value the value of the variable to create or update
 * @param {String} type the type that the value should adhere to
 * @returns {Promise} Returns a promise which resolves to the created variable
 */
/* eslint-disable-next-line object-curly-newline */
export function createOrUpdateStartupVariable({ _id, description, value, type }) {
  return generateFraasEnvironmentApi().put(
    `/variables/${_id}`,
    {
      description,
      valueBase64: btoa(value),
      expressionType: type,
    },
    requestConfig,
  );
}

/**
 * Deletes a variable
 * @param {String} _id the _id of the variable to delete
 * @returns {Promise} a promise which resolves to the deleted variable
 */
export function deleteStartupVariable(_id) {
  return generateFraasEnvironmentApi().delete(`/variables/${_id}`, requestConfig);
}

/**
 * Gets the existing secrets
 * @returns {Promise} Returns a promise that resolves to an array of secrets
 */
export function getStartupSecrets() {
  return generateFraasEnvironmentApi().get('/secrets', requestConfig);
}

/**
 * Create a secret
 * @param {String} _id the _id of the secret to create or update
 * @param {String} description the description of the secret to create or update
 * @param {String} value the value of the secret to create or update
 * @param {String} encoding the encoding of the value to create or update
 * @param {Boolean} useInPlaceholders whether the secret should be usable within placeholders
 * @returns {Promise} Returns a promise which resolves to the created secret
 */
export function createStartupSecret({
  _id,
  description,
  value,
  encoding,
  useInPlaceholders,
}) {
  return generateFraasEnvironmentApi().put(
    `/secrets/${_id}`,
    {
      encoding,
      useInPlaceholders,
      description,
      valueBase64: btoa(value),
    },
    requestConfig,
  );
}

/**
 * Updates the description on a secret
 * @param {String} _id the _id of the secret to update
 * @param {String} description the new description
 * @returns {Promise} Returns a promise which resolves to the updated secret
 */
export function updateSecretDescription({
  _id,
  description,
}) {
  return generateFraasEnvironmentApi().post(
    `/secrets/${_id}`,
    {
      description,
    },
    {
      params: {
        _action: 'setDescription',
      },
      ...requestConfig,
    },
  );
}

/**
 * Deletes a secret
 * @param {String} _id the _id of the secret to delete
 * @returns {Promise} a promise which resolves to the deleted secret
 */
export function deleteStartupSecret(_id) {
  return generateFraasEnvironmentApi().delete(`/secrets/${_id}`, requestConfig);
}

/**
 * Get the versions for a secret
 * @param {String} _id the _id of the secret to request versions for
 * @returns {Promise} a promise which resolves to an array of secret versions
 */
export function getStartupSecretVersions(_id) {
  return generateFraasEnvironmentApi().get(`/secrets/${_id}/versions`, requestConfig);
}

/**
 * Create a new version for a secret
 * @param {String} _id the _id of the secret to create a version for
 * @param {String} value the value of the new secret version
 * @returns {Promise} a promise which resolves to the created secret version
 */
export function createStartupSecretVersion(_id, value) {
  return generateFraasEnvironmentApi().post(
    `/secrets/${_id}/versions`,
    {
      valueBase64: btoa(value),
    },
    {
      params: {
        _action: 'create',
      },
      ...requestConfig,
    },
  );
}

/**
 * Sets the status of a secret
 * @param {String} _id the _id of the secret to change the status of a version for
 * @param {Number} version the version of the secret to change the status of
 * @param {Status} status the status to set for the secret version
 * @returns {Promise} a promise which resolves to the changed secret version
 */
export function setStartupSecretVersionStatus(_id, version, status) {
  return generateFraasEnvironmentApi().post(
    `/secrets/${_id}/versions/${version}`,
    { status },
    {
      params: {
        _action: 'changestatus',
      },
      ...requestConfig,
    },
  );
}

/**
 * Delete a version of a secret
 * @param {String} _id the _id of the secret to delete a version for
 * @param {Number} version the version of the secret to delete
 * @returns {Promise} a promise which resolves to the deleted secret version
 */
export function deleteStartupSecretVersion(_id, version) {
  return generateFraasEnvironmentApi().delete(`/secrets/${_id}/versions/${version}`, requestConfig);
}

/**
 * Get the current rollout restart status for the tenant
 * @returns {Promise} a promise which resolves to the current rollout restart status
 */
export function getRolloutRestartStatus() {
  return generateFraasEnvironmentApi().get('/startup', requestConfig);
}

/**
 * Initiates a new rollout restart for the tenant
 * @returns {Promise} a promise which resolves to the current rollout restart status
 */
export function beginRolloutRestart() {
  return generateFraasEnvironmentApi().post(
    '/startup',
    null,
    {
      params: {
        _action: 'restart',
      },
      ...requestConfig,
    },
  );
}
